import { ref, computed } from 'vue'

export interface GameScore {
  gridSize: number
  errors: number
  completedAt: Date
  timeToComplete?: number // in seconds
}

export interface PlayerStats {
  totalGamesCompleted: number
  bestScoreByGridSize: Record<number, GameScore>
  recentGames: GameScore[]
}

export interface GridSizeStats {
  best: number | null
  average: number | null
  gamesPlayed: number
}

const STORAGE_KEY = 'memoryku-scores'
const MAX_RECENT_GAMES = 100

export function useScoreTracking() {
  const playerStats = ref<PlayerStats>(loadPlayerStats())

  function saveGameScore(score: GameScore): void {
    const stats = playerStats.value
    
    // Update total completed games
    stats.totalGamesCompleted++
    
    // Update best score for this grid size (fewer errors is better)
    const currentBest = stats.bestScoreByGridSize[score.gridSize]
    if (!currentBest || score.errors < currentBest.errors) {
      stats.bestScoreByGridSize[score.gridSize] = { ...score }
    }
    
    // Add to recent games (keep only last 10)
    stats.recentGames.unshift({ ...score })
    if (stats.recentGames.length > MAX_RECENT_GAMES) {
      stats.recentGames = stats.recentGames.slice(0, MAX_RECENT_GAMES)
    }
    
    // Save to localStorage
    savePlayerStats(stats)
    
    console.log(`MemoryKu: Saved score - ${score.gridSize}x${score.gridSize} with ${score.errors} errors`)
  }

  function getBestScore(gridSize: number): GameScore | null {
    return playerStats.value.bestScoreByGridSize[gridSize] || null
  }

  function getRecentGames(): GameScore[] {
    return playerStats.value.recentGames
  }

  function isNewPersonalBest(gridSize: number, errors: number): boolean {
    const currentBest = getBestScore(gridSize)
    return !currentBest || errors < currentBest.errors
  }

  function getGridSizeStats(gridSize: number): GridSizeStats {
    const stats = playerStats.value
    const gamesForSize = stats.recentGames.filter(game => game.gridSize === gridSize)
    
    const best = stats.bestScoreByGridSize[gridSize]?.errors ?? null
    const average = gamesForSize.length > 0 
      ? Math.round((gamesForSize.reduce((sum, game) => sum + game.errors, 0) / gamesForSize.length) * 10) / 10
      : null
    
    return {
      best,
      average, 
      gamesPlayed: gamesForSize.length
    }
  }

  function getBestScoreText(gridSize: number): string {
    const bestScore = getBestScore(gridSize)
    if (!bestScore) {
      return 'No best score yet'
    }
    
    if (bestScore.errors === 0) {
      return 'Perfect game!'
    } else {
      const errorText = bestScore.errors === 1 ? '1 error' : `${bestScore.errors} errors`
      return `Best: ${errorText}`
    }
  }

  function clearAllScores(): void {
    localStorage.removeItem(STORAGE_KEY)
    playerStats.value = {
      totalGamesCompleted: 0,
      bestScoreByGridSize: {},
      recentGames: []
    }
    console.log('MemoryKu: All scores cleared')
  }

  function loadPlayerStats(): PlayerStats {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsed = JSON.parse(stored)
        // Convert date strings back to Date objects
        if (parsed.recentGames) {
          parsed.recentGames = parsed.recentGames.map((game: any) => ({
            ...game,
            completedAt: new Date(game.completedAt)
          }))
        }
        if (parsed.bestScoreByGridSize) {
          Object.keys(parsed.bestScoreByGridSize).forEach(key => {
            parsed.bestScoreByGridSize[key].completedAt = new Date(parsed.bestScoreByGridSize[key].completedAt)
          })
        }
        return {
          totalGamesCompleted: parsed.totalGamesCompleted || 0,
          bestScoreByGridSize: parsed.bestScoreByGridSize || {},
          recentGames: parsed.recentGames || []
        }
      }
    } catch (error) {
      console.error('MemoryKu: Error loading player stats:', error)
    }

    return {
      totalGamesCompleted: 0,
      bestScoreByGridSize: {},
      recentGames: []
    }
  }

  function savePlayerStats(stats: PlayerStats): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(stats))
    } catch (error) {
      console.error('MemoryKu: Error saving player stats:', error)
    }
  }

  // Computed properties for easy template access
  const totalGamesCompleted = computed(() => playerStats.value.totalGamesCompleted)
  const recentGames = computed(() => playerStats.value.recentGames)

  return {
    // State
    playerStats: playerStats,
    totalGamesCompleted,
    recentGames,
    
    // Methods
    saveGameScore,
    getBestScore,
    getRecentGames,
    isNewPersonalBest,
    getGridSizeStats,
    getBestScoreText,
    clearAllScores
  }
}